/**
 * Mobile Mega Menu Component
 * Mobile version mirroring desktop mega-menu layout
 */

class MobileMegaMenu {
  constructor() {
    this.menu = document.getElementById('mobile-mega-menu');
    this.overlay = document.getElementById('mobile-mega-menu-overlay');
    this.closeBtn = this.menu ? this.menu.querySelector('.mobile-mega-menu__close') : null;
    this.shopTrigger = this.menu ? this.menu.querySelector('[data-mobile-menu-trigger="shop"]') : null;
    this.clubTrigger = this.menu ? this.menu.querySelector('[data-mobile-menu-trigger="club"]') : null;
    this.submenuTriggers = this.menu ? this.menu.querySelectorAll('[data-mobile-submenu]') : [];
    this.backButtons = this.menu ? this.menu.querySelectorAll('[data-mobile-back]') : [];
    this.menuContents = this.menu ? this.menu.querySelectorAll('[data-mobile-menu-content]') : [];
    
    this.currentMenu = 'shop';
    this.currentSubmenu = null;
    this.activeSubmenuElement = null;
    
    this.init();
  }
  
  init() {
    if (!this.menu) {
      return;
    }
    
    
    // Use event delegation on the menu container for all clicks
    this.menu.addEventListener('click', (e) => {
      this.handleMenuClick(e);
    });

    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    }

    // Overlay click closes
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    
    // Escape key closes
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu && this.menu.classList.contains('is-open')) {
        this.close();
      }
    });
    
    // Expose open/close methods globally
    this.menu.open = () => this.open();
    this.menu.close = () => this.close();
    window.mobileMegaMenu = this;
    
  }

  handleMenuClick(event) {
    let target = event.target;
    

    if (!(target instanceof Element) && target && target.parentElement) {
      target = target.parentElement;
    }

    if (!(target instanceof Element)) {
      return;
    }

    const trigger = target.closest('[data-mobile-menu-trigger]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      const targetMenu = trigger.getAttribute('data-mobile-menu-trigger') || 'shop';
      this.switchMenu(targetMenu);
      return;
    }

    const submenuTrigger = target.closest('[data-mobile-submenu]');
    if (submenuTrigger) {
      event.preventDefault();
      event.stopPropagation();
      this.showSubmenu(submenuTrigger);
      return;
    }

    const backButton = target.closest('[data-mobile-back]');
    if (backButton) {
      event.preventDefault();
      event.stopPropagation();
      this.hideSubmenu();
      return;
    }

    const closeBtn = target.closest('[data-menu-close]');
    if (closeBtn) {
      event.preventDefault();
      this.close();
    }
    
  }
  
  open(menuType = 'shop') {
    if (!this.menu) return;

    
    this.menu.classList.add('is-open');
    if (this.overlay) {
      this.overlay.classList.add('is-visible');
    }
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('mobile-menu-open');
    
    
    // Switch to the requested menu and trigger animations
    this.switchMenu(menuType);
  }
  
  close() {
    if (!this.menu) return;

    this.menu.classList.remove('is-open');
    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }
    document.body.style.overflow = '';
    document.documentElement.classList.remove('mobile-menu-open');
    
    // Reset states
    this.hideSubmenu();
  }
  
  switchMenu(menuType = 'shop') {
    if (!this.menu) return;


    this.currentMenu = menuType;
    
    if (this.shopTrigger) {
      this.shopTrigger.classList.toggle('is-active', menuType === 'shop');
    }
    if (this.clubTrigger) {
      this.clubTrigger.classList.toggle('is-active', menuType === 'club');
    }
    
    this.menuContents.forEach((content) => {
      const contentType = content.getAttribute('data-mobile-menu-content');
      const isActive = contentType === menuType;
      content.style.display = isActive ? 'block' : 'none';
      content.classList.toggle('is-active', isActive);
    });
    
    this.hideSubmenu(true);
    this.restartMenuAnimations(menuType);
    
  }
  
  restartMenuAnimations(menuType) {
    if (!this.menu) return;

    const activeContent = this.menu.querySelector(`[data-mobile-menu-content="${menuType}"]`);
    if (!activeContent) return;
    
    const menuItems = activeContent.querySelectorAll('.mobile-mega-menu__menu-item');
    
    menuItems.forEach((item) => {
      item.style.animation = 'none';
      void item.offsetHeight;
      item.style.animation = '';
    });
  }
  
  restartSubmenuAnimations(submenuContainer) {
    if (!submenuContainer) return;
    
    const submenuItems = submenuContainer.querySelectorAll('.mobile-mega-menu__submenu-item');
    
    submenuItems.forEach((item) => {
      item.style.animation = 'none';
      void item.offsetHeight;
      item.style.animation = '';
    });
  }
  
  showSubmenu(trigger) {
    if (!this.menu || !trigger) return;

    const submenuId = trigger.getAttribute('data-mobile-submenu');
    if (!submenuId) return;
    
    const submenuContainer = this.menu.querySelector(`[data-mobile-submenu-content="${submenuId}"]`);
    if (!submenuContainer) return;
    
    if (this.activeSubmenuElement && this.activeSubmenuElement !== submenuContainer) {
      this.hideSubmenu(true);
    }
    
    if (submenuContainer.classList.contains('is-visible')) {
      return;
    }
    
    const contentInner = trigger.closest('.mobile-mega-menu__content-inner');
    if (contentInner) {
      contentInner.classList.add('has-submenu-open');
    }
    
    submenuContainer.style.display = 'block';
    requestAnimationFrame(() => submenuContainer.classList.add('is-visible'));
    
    this.currentSubmenu = submenuId;
    this.activeSubmenuElement = submenuContainer;
    
    this.restartSubmenuAnimations(submenuContainer);
  }
  
  hideSubmenu(immediate = false) {
    if (!this.activeSubmenuElement) return;
    
    const submenuContainer = this.activeSubmenuElement;
    submenuContainer.classList.remove('is-visible');
    
    const hideFn = () => {
      submenuContainer.style.display = 'none';
    };
    
    if (immediate) {
      hideFn();
    } else {
      setTimeout(hideFn, 250);
    }
    
    if (this.menu) {
      const contentInners = this.menu.querySelectorAll('.mobile-mega-menu__content-inner');
      contentInners.forEach((inner) => inner.classList.remove('has-submenu-open'));
    }
    
    this.currentSubmenu = null;
    this.activeSubmenuElement = null;
  }
}

// Initialize when DOM is ready or immediately if already loaded
const initMobileMegaMenu = () => {
  if (window.mobileMegaMenuInstance) return;
  window.mobileMegaMenuInstance = new MobileMegaMenu();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMobileMegaMenu);
} else {
  initMobileMegaMenu();
}
